
import { useState } from "react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { format, addDays, eachDayOfInterval, startOfToday } from "date-fns";
import { OnDemandAvailability, calculateEarliestPickup } from "@/utils/mealScheduling";
import { formatTimeRange } from "@/utils/formatTime";

interface OnDemandDatePickerProps {
  config: OnDemandAvailability;
  onDateSelected: (date: string) => void;
  onTimeSelected: (time: string) => void;
  selectedDate?: string;
  selectedTime?: string;
}

export const OnDemandDatePicker = ({
  config,
  onDateSelected,
  onTimeSelected,
  selectedDate,
  selectedTime
}: OnDemandDatePickerProps) => {
  const [visibleDates, setVisibleDates] = useState(3); // Show first 3 days initially
  const earliestDate = calculateEarliestPickup(config);
  const [selectedValue, setSelectedValue] = useState<string>(selectedDate || '');

  // Generate next 30 days
  const dates = eachDayOfInterval({
    start: startOfToday(),
    end: addDays(startOfToday(), 30)
  }).filter(date => {
    const dayName = format(date, 'EEEE');
    return config.available_days.includes(dayName) && date >= earliestDate;
  });

  const hasMoreDates = visibleDates < dates.length;

  const handleValueChange = (value: string) => {
    setSelectedValue(value);
    onDateSelected(value);
    
    // When a date is selected, also set the time to the pickup window
    if (config.pickup_window_start && config.pickup_window_end) {
      const pickupWindow = `${config.pickup_window_start} to ${config.pickup_window_end}`;
      console.log('Setting default pickup window as time:', pickupWindow);
      onTimeSelected(pickupWindow);
    } else {
      onTimeSelected('12:00');
    }
  };

  // Format the pickup window for display
  const formattedPickupWindow = formatTimeRange(
    config.pickup_window_start, 
    config.pickup_window_end,
    " to "
  );

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium">Select pickup date</h4>
      <ScrollArea className="rounded-md border p-4">
        <RadioGroup
          value={selectedValue}
          onValueChange={handleValueChange}
          className="grid gap-2"
        >
          {dates.slice(0, visibleDates).map((date) => {
            const dateStr = format(date, 'yyyy-MM-dd');
            const displayDate = format(date, "EEEE, MMMM d, yyyy");
            
            return (
              <div
                key={dateStr}
                className="flex items-center space-x-2 rounded-md border p-3 hover:bg-accent"
              >
                <RadioGroupItem value={dateStr} id={dateStr} />
                <Label htmlFor={dateStr} className="flex-grow cursor-pointer">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
                    <span>{displayDate}</span>
                    {dateStr === selectedValue && (
                      <span className="text-xs text-muted-foreground">
                        Pickup: {formattedPickupWindow}
                      </span>
                    )}
                  </div>
                </Label>
              </div>
            );
          })}
        </RadioGroup>
        
        {hasMoreDates && (
          <Button
            variant="ghost"
            className="w-full mt-4 flex items-center justify-center gap-2"
            onClick={() => setVisibleDates(prev => prev + 3)}
          >
            Show additional availability
            <ChevronDown className="h-4 w-4" />
          </Button>
        )}
      </ScrollArea>
    </div>
  );
};
